// File: src/components/auth/LoginForm.tsx
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { 
  TextField, 
  Button, 
  Stack, 
  InputAdornment, 
  IconButton,
  useTheme,
  Divider,
  Typography,
  Alert,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Visibility, 
  VisibilityOff, 
  Email as EmailIcon, 
  Lock as LockIcon,
  Google as GoogleIcon,
  Microsoft as MicrosoftIcon
} from '@mui/icons-material';
import { formButtonStyles } from '@/styles/formStyles';
import { SSOConfig, LoginCredentials, PasswordLoginCredentials, LoginError } from '@/services/api/domains/auth';
import { SSOService } from '@/services/auth/SSOService';

const loginSchema = z.object({
  email: z.string().email('Invalid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSubmit: (data: LoginCredentials) => Promise<void>;
}

export default function LoginForm({ onSubmit }: LoginFormProps) {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);
  const [ssoConfig, setSsoConfig] = useState<SSOConfig | null>(null);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const handlePasswordLogin = async (data: LoginFormData) => {
    try {
      const credentials: PasswordLoginCredentials = {
        email: data.email,
        password: data.password
      };
      await onSubmit(credentials);
    } catch (error) {
      const loginError = error as LoginError;
      if (loginError.sso_config) {
        setSsoConfig(loginError.sso_config);
        setSsoError('This account requires SSO login. Please use the appropriate SSO button below.');
      } else {
        setError('root', { message: loginError.message });
      }
    }
  };

  const handleSSOLogin = async (provider: 'google' | 'azure') => {
    if (!ssoConfig || ssoConfig.provider !== provider) {
      setSsoError('Invalid SSO configuration');
      return;
    }

    try {
      setSsoError(null);
      const { token, domain } = await SSOService.getToken(ssoConfig);
      await onSubmit({ sso_token: token, district_domain: domain });
    } catch (error) {
      setSsoError('SSO login failed. Please try again.');
      console.error('SSO error:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit(handlePasswordLogin)}>
      <Stack spacing={3}>
        {errors.root && (
          <Alert severity="error">{errors.root.message}</Alert>
        )}
        
        {ssoError && (
          <Alert severity="error">{ssoError}</Alert>
        )}

        <TextField
          {...register('email')}
          label="Email"
          error={!!errors.email}
          helperText={errors.email?.message}
          fullWidth
          type="email"
          autoComplete="email"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          {...register('password')}
          label="Password"
          type={showPassword ? 'text' : 'password'}
          error={!!errors.password}
          helperText={errors.password?.message}
          fullWidth
          autoComplete="current-password"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  size="large"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isSubmitting}
          fullWidth
          sx={formButtonStyles(theme)}
        >
          {isSubmitting ? 'Signing in...' : 'Sign in with Password'}
        </Button>

        {ssoConfig && (
          <>
            <Divider>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>

            {ssoConfig.provider === 'google' && (
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => handleSSOLogin('google')}
                startIcon={<GoogleIcon />}
                sx={{
                  borderColor: '#4285f4',
                  color: '#4285f4',
                  '&:hover': {
                    borderColor: '#2b6cd4',
                    backgroundColor: 'rgba(66, 133, 244, 0.04)',
                  },
                }}
              >
                Sign in with Google
              </Button>
            )}

            {ssoConfig.provider === 'azure' && (
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => handleSSOLogin('azure')}
                startIcon={<MicrosoftIcon />}
                sx={{
                  borderColor: '#0078d4',
                  color: '#0078d4',
                  '&:hover': {
                    borderColor: '#005a9e',
                    backgroundColor: 'rgba(0, 120, 212, 0.04)',
                  },
                }}
              >
                Sign in with Microsoft
              </Button>
            )}
          </>
        )}
      </Stack>
    </form>
  );
}